
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Save, Plus, Clock, Tag, Trash2, Phone } from 'lucide-react';
import { useTwilioContext } from '@/context/TwilioContext';
import { apiConfig } from '@/lib/env';
import { useToast } from '@/hooks/use-toast';

interface CallNote {
  id: number;
  user_id: number;
  call_sid?: string;
  call_log_id?: number;
  content: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

interface CallNotesProps {
  phoneNumber?: string;
}

export const CallNotes = ({ phoneNumber }: CallNotesProps) => {
  const [notes, setNotes] = useState<CallNote[]>([]);
  const [currentNote, setCurrentNote] = useState('');
  const [currentTags, setCurrentTags] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { callInProgress, callSid } = useTwilioContext();
  const { toast } = useToast();
  const hasFetched = useRef(false);
  
  // Mock user ID - in real app this would come from auth context
  const userId = 1;

  // Auto-populate note with phone number when available and note is empty
  useEffect(() => {
    if (phoneNumber && phoneNumber.trim() !== '' && currentNote === '') {
      setCurrentNote(`Call to ${phoneNumber}\n\n`);
    }
  }, [phoneNumber, currentNote]);

  // Load notes from backend - only once per component mount
  useEffect(() => {
    if (hasFetched.current) return;
    
    const fetchNotes = async () => {
      console.log('CallNotes: Fetching notes for user:', userId);
      try {
        setIsLoading(true);
        const response = await fetch(`${apiConfig.baseUrl}/notes/user/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setNotes(data);
          console.log('CallNotes: Successfully loaded notes:', data.length);
        }
      } catch (error) {
        console.error('CallNotes: Error fetching notes:', error);
        toast({
          title: "Error",
          description: "Failed to load notes",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
        hasFetched.current = true;
      }
    };

    fetchNotes();
  }, []); // Empty dependency array - only run once

  const saveNote = async () => {
    if (!currentNote.trim()) return;

    setIsSaving(true);

    const noteData = {
      user_id: userId,
      call_sid: callSid || null,
      call_log_id: null,
      content: currentNote.trim(),
      tags: currentTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)
    };

    try {
      const response = await fetch(`${apiConfig.baseUrl}/notes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData)
      });

      if (response.ok) {
        const newNote = await response.json();
        setNotes(prev => [newNote, ...prev]);
        setCurrentNote('');
        setCurrentTags('');
        toast({
          title: "Success",
          description: "Note saved successfully"
        });
      } else {
        throw new Error('Failed to save note');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      toast({
        title: "Error",
        description: "Failed to save note",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteNote = async (noteId: number) => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}/notes/${noteId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setNotes(prev => prev.filter(note => note.id !== noteId));
        toast({
          title: "Success",
          description: "Note deleted successfully"
        });
      } else {
        throw new Error('Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: "Error",
        description: "Failed to delete note",
        variant: "destructive"
      });
    }
  };

  const quickTags = ['follow-up', 'interested', 'not-interested', 'callback', 'pricing', 'demo'];

  const addQuickTag = (tag: string) => {
    const currentTagsList = currentTags
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    
    if (!currentTagsList.includes(tag)) {
      const newTags = [...currentTagsList, tag].join(', ');
      setCurrentTags(newTags);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Check if we have a phone number from the dialer
  const hasPhoneNumber = phoneNumber && phoneNumber.trim() !== '';
  
  // Enable save button if there's note content OR if there's a phone number (even with empty note)
  const canSave = currentNote.trim() || hasPhoneNumber;

  return (
    <div className="h-full flex flex-col">
      {/* Note Input Section */}
      <Card className="border-0 border-b rounded-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Call Notes
            {callInProgress && (
              <Badge variant="outline" className="text-xs">
                Live Call
              </Badge>
            )}
            {hasPhoneNumber && !callInProgress && (
              <Badge variant="secondary" className="text-xs flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {phoneNumber}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder={hasPhoneNumber ? `Notes for ${phoneNumber}...` : "Take notes during your call..."}
            value={currentNote}
            onChange={(e) => setCurrentNote(e.target.value)}
            className="min-h-[80px] resize-none"
          />
          
          <Input
            placeholder="Add tags (comma separated)"
            value={currentTags}
            onChange={(e) => setCurrentTags(e.target.value)}
          />
          
          {/* Quick Tags */}
          <div className="flex flex-wrap gap-1">
            {quickTags.map(tag => (
              <Button
                key={tag}
                variant="outline"
                size="sm"
                className="text-xs h-6"
                onClick={() => addQuickTag(tag)}
              >
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </Button>
            ))}
          </div>
          
          <Button 
            onClick={saveNote}
            disabled={!canSave || isSaving}
            className="w-full"
            size="sm"
          >
            {isSaving ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Note
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Notes List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {isLoading ? (
            <div className="text-center py-8">
              <Clock className="h-8 w-8 mx-auto mb-4 text-muted-foreground animate-spin" />
              <p className="text-muted-foreground text-sm">Loading notes...</p>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground text-sm">
                No notes yet. Start taking notes during your calls!
              </p>
            </div>
          ) : (
            notes.map((note) => (
              <Card key={note.id} className="p-3">
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatDateTime(note.created_at)}
                      {note.call_sid && (
                        <Badge variant="outline" className="text-xs">
                          Call: {note.call_sid.slice(-8)}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteNote(note.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  
                  <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                  
                  {note.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {note.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
